import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * The clock a loop runs on when something outside the screen keeps it: a
 * count of plays, moved on by whatever owns the clock, and a way to tell that
 * owner how long one play is. See LoopFrame, which draws the bar and buttons
 * for it on the flows page's interactions.
 */
export const LoopClock = createContext<{
  run: number;
  register: (every: number) => void;
} | null>(null);

/**
 * A screen playing its one movement over and over.
 *
 * The prototype already knows how to replay a transition — the chat hands a
 * stage a way to drop the next question's bar in and bring the current one
 * back, which is the button on the intake stage. This calls that on an interval
 * instead, so a handover can show the movement to somebody reading the page
 * rather than telling them to go and press something.
 *
 * A real screen moving, not a recording of one: whatever the prototype does now
 * is what the page shows.
 *
 * The interval belongs to the mount, and a mini only mounts once it is near the
 * viewport, so nothing off screen is animating. Inside a LoopClock the clock's
 * owner decides when the next play is instead, so it can be paused, shown and
 * stopped from outside the screen.
 */
const Looping = ({
  every = 2600,
  remount = false,
  render,
}: {
  /**
   * Start the screen again from scratch each time instead of calling its
   * replay handle. For a movement the screen plays once on its own as it
   * opens, like a harness answering a question, which has nothing to replay.
   */
  remount?: boolean;
  /**
   * How long between plays. Long enough to watch the movement, land, and read
   * as a rest rather than a stutter — the bar and the orb together take about
   * 600ms.
   */
  every?: number;
  /**
   * The screen, handed the registrar to give its replay handle to. Whatever the
   * prototype passes here is what gets called.
   */
  render: (onReplay: (play: () => void) => void) => ReactNode;
}) => {
  const clock = useContext(LoopClock);
  const play = useRef<(() => void) | undefined>(undefined);
  const [ownRun, setOwnRun] = useState(0);
  const run = clock ? clock.run : ownRun;

  useEffect(() => {
    clock?.register(every);
  }, [clock, every]);

  // Its own clock, where nothing outside keeps one.
  useEffect(() => {
    if (clock) return;
    const id = window.setInterval(() => setOwnRun((n) => n + 1), every);
    return () => window.clearInterval(id);
  }, [clock, every]);

  // Each play after the first: a replay, unless the screen starts again.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!remount) play.current?.();
  }, [run, remount]);

  return (
    <Fragment key={remount ? run : 0}>
      {render((fn) => {
        play.current = fn;
      })}
    </Fragment>
  );
};

Looping.displayName = 'Looping';
export default Looping;
